// pages/products/traffic-safety/CarrierEmergencySign.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import HeroSection from '../../../components/ProductsDetail/HeroSection';
import ProductShowcaseSection from '../../../components/ProductsDetail/ProductShowcaseSection';
import CTASection from '../../../components/ProductsDetail/CTASection';
import {
  InfoSection,
  FeatureList,
  SpecTable,
  SupplyTable,
  UsageSteps,
} from '../../../components/ProductsDetail/InfoBlocks';
import SectionDivider from '../../../components/ProductsDetail/SectionDivider';
import ProductDetailNav from '../../../components/ProductsDetail/ProductDetailNav';

const CarrierEmergencySign = () => {

  // ① 사양표 데이터
  const specRows = [
    { label: '제품명', value: '캐리어형 비상표시장치' },
    { label: '모델명', value: 'SG-905' },
    { label: '제품크기', value: '450*380*165(h)mm(±10)' },
    { label: '에어타워 높이', value: '2530(h)*380(w)mm(±5)\n※ 사용 목적에 따라 에어타워 문구 교체 가능' },
    { label: '중량', value: '12kg(±1)' },
    { label: '사용시간', value: '완충 시 7시간 이상 사용\n단, 사이렌/LED 사용 여부에 따라 효율 감소' },
    { label: '충전시간', value: '완충 시 4시간 소요' },
    { label: '충전전압', value: '15V / 6A' },
  ];

  const features = [
    {
      title: '원터치 조작으로 즉시 설치',
      bullets: [
      {
        label: (
          <>장치 개방 후 전원 버튼 한 번으로 작동하여 <strong>초기 설치 시간을 대폭 단축</strong></>
        ),
      },
      {
        label: (
          <>복잡한 조작 없이 누구나 빠르게 배치 가능</>
        ),
      },
      {
        label: (
          <>도로 위 체류 시간을 최소화해 근무자 안전성 향상</>
        ),
      },
      ]
    },
    {
      title: '우수한 시인성으로 2차 사고 예방',
      bullets: [
      {
        label: (
          <>고휘도 LED 및 고대비 패널을 적용하여 <strong>주·야간·악천후에도 뛰어난 시인성 제공</strong></>
        ),
      },
      {
        label: (
          <>후방 운전자에게 전방 위험 및 통제 방향을 명확하게 전달</>
        ),
      },
      {
        label: (
          <>급작스러운 정체 구간에서도 조기 인지 가능</>
        ),
      },
      ]
    },
    {
      title: '경량·캐리어형 이동 구조',
      bullets: [
      {
        label: (
          <>바퀴가 장착된 <strong>캐리어형 설계</strong>로 누구나 손쉽게 이동 및 배치</>
        ),
      },
      {
        label: (
          <>차량 적재 및 이동이 간편하여 긴급 상황에서도 즉각적인 대응 가능</>
        ),
      },
      {
        label: (
          <>현장 작업자 부담 감소 및 작업 효율성 향상</>
        ),
      },
      ]
    },
  ];

  // ② 납품실적 데이터 (표의 연도 열 순서 정의)
  const years = [2018, 2019, 2020, 2021, 2022, 2023, 2024];
  const supplyRows = [
    { org: '한국도로공사 및 도로유지보수 민간업체', 2018: 40, 2019: 146, 2020: 76, 2021: 139, 2022: 276, 2023: 209, 2024: 115,},
    { org: '경찰청 등 공공기관 및 지자체', 2018: 0, 2019: 19, 2020: 209, 2021: 58, 2022: 33, 2023: 22, 2024: 65,},
    { org: '기타', 2018: 1, 2019: 44, 2020: 56, 2021: 4, 2022: 13, 2023: 10, 2024: 30,},
    { org: '합계', 2018: 41, 2019: 209, 2020: 341, 2021: 201, 2022: 322, 2023: 241, 2024: 210,},
  ];

  // ③ 활용범위 카드
  const steps = [
    { image: '/images/products/traffic-safety/carrier-emergency-sign1.webp', text: '고속도로 순찰단속 시' },
    { image: '/images/products/traffic-safety/carrier-emergency-sign2.webp', text: '도로공사 및 정비 시' },
    { image: '/images/products/traffic-safety/carrier-emergency-sign3.webp', text: '교통사고 처리 시' },
    { image: '/images/products/traffic-safety/carrier-emergency-sign4.webp', text: '기타 긴급상황 발생 시' },
  ];
  
  return (
    <>
      <HeroSection
        title={`캐리어형 비상표시장치`}
        subtitle={`신속한 경보와 높은 시인성으로 \n작업자와 운전자의 안전을 지켜드립니다`}
        backgroundImage="/images/products/traffic-safety.webp"
        breadcrumbs={[
            { label: '홈', path: '/' },
            { label: '도로 안전경보시스템', path: '/products/traffic-safety' },
            { label: '캐리어형 비상표시장치' }, // 현재 페이지는 path 없음
        ]}
        categories={[
          { label: '도로 안전경보시스템', path: '/products/traffic-safety' },
          { label: '도로 안전유도시설', path: '/products/guardrail' },
          { label: '도로환경 개선시설', path: '/products/facilities' },
          { label: '도로 안내표지시스템', path: '/products/signage' },
        ]}
      />

      <ProductShowcaseSection
        subtitle={`도로 위의 안전알림장치`}
        title={`캐리어형 비상표시장치`}
        description={
          <>
            도로 유지보수, 긴급 공사, 교통사고 처리 등{' '}
            도로 통제 상황 시 <strong>후방 차량에 전방 상황을 명확히 전달</strong>하여 <strong>2차 사고를 예방하고{' '}
            근무자의 안전을 확보</strong>하기 위한 장치로서, 교통 흐름을 원활히 유도합니다.
            <br />
            <br />
            장치 개방 후 <strong>원터치 전원버튼만으로 설치가 즉시 완료</strong>되어,{' '}
            바쁜 현장에서 <strong>도로 위 노출 시간을 최소화</strong>하며, 대형사고 위험에 노출된{' '}
            <strong>현장 출동 근무자 및 운전자의 안전을 보다 신속하고 효율적으로 보장</strong>합니다.
          </>
        }
        videoSrc="/videos/carrier-emergency-sign.mp4"
        badgeImages={[
            { src: "/images/logo/venture.webp", scale: 0.95 },   // 너무 크게 보이면 살짝 축소
            { src: "/images/logo/ventureGoods.webp", scale: 1.5},                 // 기본 1.0
            { src: "/images/logo/disaster.webp", scale: 1.5 },
        ]}
      />

      <SectionDivider />

      <InfoSection title="제품설명">
        <SpecTable rows={specRows} />
      </InfoSection>


      <InfoSection title="납품실적">
        <SupplyTable years={years} rows={supplyRows} aggregation="count"/>
      </InfoSection>

      <InfoSection
        title="활용범위"
        subtitle="사용 목적에 따라 손쉽게 에어타워 문구 교체가 가능합니다"
      >
        <UsageSteps steps={steps} />
      </InfoSection>

      <InfoSection title="제품 특징">
        <FeatureList features={features} />
      </InfoSection>

      <ProductDetailNav
        category="traffic-safety"
        currentPath="/products/traffic-safety/carrier-emergency-sign"
        listPath="/products/traffic-safety"
      />

      <CTASection />
    </>
  );
};

export default CarrierEmergencySign;
